import i18n from 'i18next';

import { ProgressStepName } from '../enums/ProgressStepName';
import { ProgressStep } from '../types/ProgressStep';
import { APPROVED_BY_LENDER_STATUSES } from './approvedByLenderStatuses';
import { COMPLETE_APPLICATION_STATUSES } from './completeApplicationStatuses';
import { FINALIZING_STIPS_STATUSES } from './finalizingStipsStatuses';
import { LENDER_FUNDED_STATUSES } from './lenderFundedStatuses';
import { NEW_APPLICATION_STATUSES } from './newApplicationStatuses';
import { PREPARING_CONTRACTS_STATUSES } from './preparingContractsStatuses';
import { REVIEWING_DOCUMENTS_STATUSES } from './reviewingDocumentsStatuses';
import { SIGNING_CONTRACTS_STATUSES } from './signingContractsStatuses';
import { SUBMITTED_TO_LENDER_STATUSES } from './submittedToLenderStatuses';
import { WAITING_ON_FUNDING_STATUSES } from './waitingOnFundingStatuses';

/**
 * An object that maps each step in the application progress to its corresponding details.
 * Each key in the object is a an enum value representing a step in the progress.
 * The value for each key contains:
 *
 * - `applicationStatuses`: an array of application statuses that correspond to this step
 * - `completed`: a boolean indicating whether this step has been completed
 * - `description`: a localized string that describes this step
 * - `title`: a localized string that provides a title for this step
 */
export const steps: Record<ProgressStepName, ProgressStep> = {
  [ProgressStepName.NEW_APPLICATION]: {
    applicationStatuses: NEW_APPLICATION_STATUSES,
    completed: false,
    description: i18n.t('applicationProgress.newApplication.description'),
    title: i18n.t('applicationProgress.newApplication.title'),
  },
  [ProgressStepName.COMPLETE_APPLICATION]: {
    applicationStatuses: COMPLETE_APPLICATION_STATUSES,
    completed: false,
    description: i18n.t('applicationProgress.completeApplication.description'),
    title: i18n.t('applicationProgress.completeApplication.title'),
  },
  [ProgressStepName.SUBMITTED_TO_LENDER]: {
    applicationStatuses: SUBMITTED_TO_LENDER_STATUSES,
    completed: false,
    description: i18n.t('applicationProgress.submittedToLender.description'),
    title: i18n.t('applicationProgress.submittedToLender.title'),
  },
  [ProgressStepName.APPROVED_BY_LENDER]: {
    applicationStatuses: APPROVED_BY_LENDER_STATUSES,
    completed: false,
    description: i18n.t('applicationProgress.approvedByLender.description'),
    title: i18n.t('applicationProgress.approvedByLender.title'),
  },
  [ProgressStepName.PREPARING_CONTRACTS]: {
    applicationStatuses: PREPARING_CONTRACTS_STATUSES,
    completed: false,
    description: i18n.t('applicationProgress.preparingContracts.description'),
    title: i18n.t('applicationProgress.preparingContracts.title'),
  },
  [ProgressStepName.SIGNING_CONTRACTS]: {
    applicationStatuses: SIGNING_CONTRACTS_STATUSES,
    completed: false,
    description: i18n.t('applicationProgress.signingContracts.description'),
    title: i18n.t('applicationProgress.signingContracts.title'),
  },
  [ProgressStepName.REVIEWING_DOCUMENTS]: {
    applicationStatuses: REVIEWING_DOCUMENTS_STATUSES,
    completed: false,
    description: i18n.t('applicationProgress.reviewingDocuments.description'),
    title: i18n.t('applicationProgress.reviewingDocuments.title'),
  },
  [ProgressStepName.FINALIZING_STIPS]: {
    applicationStatuses: FINALIZING_STIPS_STATUSES,
    completed: false,
    description: i18n.t('applicationProgress.finalizingStips.description'),
    title: i18n.t('applicationProgress.finalizingStips.title'),
  },
  [ProgressStepName.WAITING_ON_FUNDING]: {
    applicationStatuses: WAITING_ON_FUNDING_STATUSES,
    completed: false,
    description: i18n.t('applicationProgress.waitingOnFunding.description'),
    title: i18n.t('applicationProgress.waitingOnFunding.title'),
  },
  [ProgressStepName.LENDER_FUNDED]: {
    applicationStatuses: LENDER_FUNDED_STATUSES,
    completed: false,
    description: i18n.t('applicationProgress.lenderFunded.description'),
    title: i18n.t('applicationProgress.lenderFunded.title'),
  },
};
